// Vendor imports
const _ = require('lodash')
const React = require('react')
const PropTypes = require('prop-types')
const classNames = require('classnames')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
// Container imports
const ArticleProfileDetailCard = require('./detail-card-smart')
const ArticleProfileRelated = require('./related-smart')

// BEM constants
const bem = BemEntity('ArticleProfileModal')

// Swipe constants
const MAX_SWIPE_WIDTH_FACTOR = 0.9
const MIN_SWIPE_WIDTH_FACTOR = 0.25
const BROWSER_BACK_SWIPE_RANGE = 40 // px
const KEY_SCROLL_AMOUNT = 100 // px
const SwipeDirectionEnum = {
  NONE: 'none',
  LEFT: 'left',
  RIGHT: 'right'
}

// Keycode constants
const CLOSE_MODAL_KEYCODE = 27 // Escape
const SWIPE_LEFT_KEYCODE = 37 // Left Arrow
const SWIPE_RIGHT_KEYCODE = 39 // Right Arrow
const SCROLL_UP_KEYCODE = 38 // Up Arrow
const SCROLL_DOWN_KEYCODE = 40 // Down Arrow
const captureKeyCodes = [
  CLOSE_MODAL_KEYCODE,
  SWIPE_LEFT_KEYCODE,
  SWIPE_RIGHT_KEYCODE,
  SCROLL_UP_KEYCODE,
  SCROLL_DOWN_KEYCODE
]

// Constructor constants
const bindMethods = [
  'onCloseCapture',
  'onKeyDown',
  'onSwipeStart',
  'onSwipeMove',
  'onSwipeEnd'
]
const propMethods = [
  'onClose',
  'onSlideRefresh',
  'onSwipeLeft',
  'onSwipeRight'
]
const debounceMethods = [
  'onSwipeLeft',
  'onSwipeRight'
]
const DEBOUNCE_DELAY = 300 // ms
const DEBOUNCE_OPTIONS = { leading: true, trailing: false }

class ArticleProfileModal extends React.PureComponent {
  constructor(props) {
    super(props)

    _.bindAll(this, bindMethods)
    propMethods.forEach(key => {
      this[key] = () => this.props[key]()
    })
    debounceMethods.forEach(key => {
      this[key] = _.debounce(this[key], DEBOUNCE_DELAY, DEBOUNCE_OPTIONS)
    })

    this.state = {
      isSwiping: false,
      swipeDirection: SwipeDirectionEnum.NONE,
      swipeWidth: 0
    }
    this.currentSlideRef = React.createRef()
  }

  scrollCurrentSlide(scrollAmount) {
    const slideEl = this.currentSlideRef.current
    if (slideEl && scrollAmount) {
      slideEl.scrollTop += scrollAmount
    }
  }

  // Swipe handling methods

  setSwipeDirection(swipeDirection) {
    this.setState(() => ({ swipeDirection }))
  }

  onSwipeStart(event) {
    const currentSlideEl = this.currentSlideRef.current
    if (!this.props.isVisible || !currentSlideEl || this.state.isSwiping) {
      return
    }
    const { width } = currentSlideEl.getBoundingClientRect()
    this._swipeTouchStartX = _.get(event, 'touches[0].pageX')
    this._swipeTouchStartY = _.get(event, 'touches[0].pageY')
    this._swipeMaxWidth = width * MAX_SWIPE_WIDTH_FACTOR
    this.setState(() => ({
      isSwiping: true,
      swipeDirection: SwipeDirectionEnum.NONE,
      swipeWidth: 0
    }))
  }

  onSwipeMove(event) {
    if (!this.state.isSwiping) {
      return
    }
    // Determine the delta of the movement
    const moveX = _.get(event, 'touches[0].pageX')
    const moveY = _.get(event, 'touches[0].pageY')
    const diffX = this._swipeTouchStartX - moveX
    const diffY = this._swipeTouchStartY - moveY
    // Cancel the swipe if there's a more-important gesture
    const isVerticalSwipe = Math.abs(diffY) > Math.abs(diffX)
    const isBrowserBackSwipe = this._swipeTouchStartX < BROWSER_BACK_SWIPE_RANGE
    if (isVerticalSwipe || isBrowserBackSwipe) {
      this.setState(() => ({ swipeWidth: 0 }))
      return
    }
    // Determine which slide to swipe in and how far
    let swipeWidth = 0
    if (diffX < 0) {
      swipeWidth = Math.min(-diffX, this._swipeMaxWidth)
    } else if (diffX > 0) {
      swipeWidth = Math.max(-diffX, -this._swipeMaxWidth)
    }
    // Peek the next/previous slide in by the swipe amount
    this.setState(() => ({ swipeWidth }))
  }

  onSwipeEnd() {
    const { isSwiping, swipeWidth } = this.state
    if (!isSwiping) {
      return
    }
    const threshold = this._swipeMaxWidth * MIN_SWIPE_WIDTH_FACTOR
    // Clear swipe state
    delete this._swipeTouchStartX
    delete this._swipeTouchStartY
    delete this._swipeMaxWidth
    this.setState(() => ({
      isSwiping: false,
      swipeWidth: 0
    }))
    // Determine if swipe triggered index update
    if (Math.abs(swipeWidth) > threshold) {
      if (swipeWidth > 0) {
        this.onSwipeLeft()
      }
      if (swipeWidth < 0) {
        this.onSwipeRight()
      }
    }
  }

  // Other event handling methods

  onCloseCapture(event) {
    const targetClassNames = event.target.className.split(' ')
    const captureClassName = bem.element('slide-outer')
    if (targetClassNames.includes(captureClassName)) {
      event.preventDefault()
      event.stopPropagation()
      this.onClose()
    }
  }

  onKeyDown(event) {
    // Do nothing if the modal is not currently being shown
    if (!this.props.isVisible) {
      return
    }
    const { keyCode, target: { tagName } } = event
    // Ignore keyCodes that the modal is not interested in
    if (!captureKeyCodes.includes(keyCode)) {
      return
    }
    // Ensure text-capturing inputs keep doing their thing
    if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
      return
    }
    event.preventDefault()
    switch (keyCode) {
      case CLOSE_MODAL_KEYCODE:
        this.onClose()
        break
      case SWIPE_LEFT_KEYCODE:
        this.onSwipeLeft()
        break
      case SWIPE_RIGHT_KEYCODE:
        this.onSwipeRight()
        break
      case SCROLL_UP_KEYCODE:
        this.scrollCurrentSlide(-KEY_SCROLL_AMOUNT)
        break
      case SCROLL_DOWN_KEYCODE:
        this.scrollCurrentSlide(KEY_SCROLL_AMOUNT)
        break
    }
  }

  // React Component lifecycle methods

  componentDidMount() {
    if (typeof window === 'object') {
      window.addEventListener('keydown', this.onKeyDown)
      document.addEventListener('touchstart', this.onSwipeStart)
      document.addEventListener('touchmove', this.onSwipeMove)
      document.addEventListener('touchend', this.onSwipeEnd)
    }
  }

  componentWillUnmount() {
    if (typeof window === 'object') {
      window.removeEventListener('keydown', this.onKeyDown)
      document.removeEventListener('touchstart', this.onSwipeStart)
      document.removeEventListener('touchmove', this.onSwipeMove)
      document.removeEventListener('touchend', this.onSwipeEnd)
    }
  }

  componentDidUpdate(prevProps) {
    const indexDiff = prevProps.currentIndex - this.props.currentIndex
    const depthDiff = prevProps.modalDepth - this.props.modalDepth
    const countDiff = prevProps.slideCount - this.props.slideCount
    if (!this.props.slideCount) {
      // Close the modal if there are no slides to show
      this.onClose()
    } else if (countDiff >= 1 && !depthDiff) {
      // Refresh the current slide if slides have been removed
      this.onSlideRefresh()
    } else if (Math.abs(indexDiff) > 1 || depthDiff) {
      // Prevent sliding behavior when showing related article profile modal
      this.setSwipeDirection(SwipeDirectionEnum.NONE)
    } else if (indexDiff > 0) {
      this.setSwipeDirection(SwipeDirectionEnum.LEFT)
    } else if (indexDiff < 0) {
      this.setSwipeDirection(SwipeDirectionEnum.RIGHT)
    }
  }

  // React Render methods

  renderBackdrop() {
    return (
      tag('div', {
        className: bem.element('backdrop'),
        onClick: this.onClose
      })
    )
  }

  renderCloseButton() {
    return (
      tag('div', {
        className: bem.element('close-container')
      },
        tag('button', {
          className: bem.element('close-button'),
          onClick: this.onClose
        })
      )
    )
  }

  renderContent() {
    const { isSwiping, swipeWidth, swipeDirection } = this.state
    const transform = `translateX(${swipeWidth}px)`
    return (
      tag('div', {
        className: bem.element('content', {
          swiping: isSwiping,
          direction: swipeDirection
        }),
        style: { transform }
      },
        [
          this.renderSlide(-1),
          this.renderSlide(0),
          this.renderSlide(+1)
        ]
      )
    )
  }

  renderSlide(slideIndexOffset = 0) {
    const { currentIndex, modalDepth, slideCount } = this.props
    const slideIndex = currentIndex + slideIndexOffset
    if (slideIndex < 0 || slideIndex >= slideCount) {
      return null
    }
    return (
      tag('div', {
        // There is no unique key; falling back to index
        key: `${modalDepth}:${slideIndex}`,
        ref: slideIndexOffset ? null : this.currentSlideRef,
        // Outer is responsible for the full-window scrolling
        className: bem.element('slide-outer', {
          previous: slideIndexOffset < 0,
          current: slideIndexOffset == 0,
          next: slideIndexOffset > 0
        }),
        onClickCapture: this.onCloseCapture
      },
        tag('div', {
          // Inner is responsible for centered content
          className: bem.element('slide-inner')
        },
          tag(ArticleProfileDetailCard, { slideIndex }),
          tag(ArticleProfileRelated, { slideIndex })
        )
      )
    )
  }

  renderSwipeLeftButton() {
    const { currentIndex } = this.props
    if (currentIndex <= 0) {
      return null
    }
    return (
      tag('button', {
        type: 'button',
        className: bem.element('swipe-button', 'left'),
        onClick: this.onSwipeLeft
      })
    )
  }

  renderSwipeRightButton() {
    const { currentIndex, slideCount } = this.props
    if (currentIndex + 1 >= slideCount) {
      return
    }
    return (
      tag('button', {
        type: 'button',
        className: bem.element('swipe-button', 'right'),
        onClick: this.onSwipeRight
      })
    )
  }

  render() {
    if (!this.props.isVisible) {
      return null
    }
    return (
      tag('div', {
        className: classNames(
          this.props.className,
          bem.block()
        )
      },
        this.renderBackdrop(),
        this.renderContent(),
        this.renderCloseButton(),
        this.renderSwipeLeftButton(),
        this.renderSwipeRightButton()
      )
    )
  }
}

ArticleProfileModal.propTypes = {
  // Flags
  isVisible: PropTypes.bool.isRequired,
  // Values
  className: PropTypes.string,
  currentIndex: PropTypes.number.isRequired,
  modalDepth: PropTypes.number.isRequired,
  slideCount: PropTypes.number.isRequired,
  // Callbacks
  onClose: PropTypes.func.isRequired,
  onSlideRefresh: PropTypes.func.isRequired,
  onSwipeLeft: PropTypes.func.isRequired,
  onSwipeRight: PropTypes.func.isRequired
}

module.exports = ArticleProfileModal
